import type { Chat } from '@jerome2/shared';
import { createStore, persist } from '../lib/store.ts';
import { app } from './app.ts';

// Panelen en tabbladen: één of twee panelen, elk met tabbladen. Elk tabblad heeft een soort
// (mail, chat, …) en eigen toestand die het scherm bijhoudt (filter, map, open mail, gesprek).
// Op de telefoon is er maar één paneel in beeld: een nieuw tabblad gaat daar altijd naar links.

export type TabKind = 'mail' | 'chat' | 'agenda' | 'rules' | 'dev' | 'settings' | 'knowledge' | 'usage' | 'briefing' | 'releases' | 'excel' | 'whatsapp';
export type PaneId = 'left' | 'right';

export interface Tab {
  id: string;
  kind: TabKind;
  title: string;
  state: Record<string, unknown>;
}

export interface Pane {
  id: PaneId;
  tabIds: string[];
  activeTabId: string | null;
}

export interface TabsState {
  tabs: Record<string, Tab>;
  panes: Record<PaneId, Pane>;
  paneCount: 1 | 2;
  activePane: PaneId;
  /** De gesprekken met een antwoord van Jerome dat de eigenaar nog niet zag (ticket 30961, deel 2): de stip op hun tabblad en bij
   * hun rij in de gesprekkenlijst, tot het gesprek in beeld is terwijl het venster zichtbaar is (state/chatTurns.ts). Per gesprek,
   * niet per tabblad: een gesprek zonder tabblad heeft alleen een rij, en twee tabbladen van één gesprek horen bij één antwoord. */
  unread?: string[];
}

/** Hooguit zoveel ongelezen gesprekken in de tabstaat (de nieuwste): een verwijderd gesprek blijft er anders voor altijd in staan. */
export const UNREAD_KEPT = 50;

export const TAB_TITLES: Record<TabKind, string> = { mail: 'Mail', chat: 'Chat', agenda: 'Agenda', rules: 'Regels', dev: 'Ontwikkelen', settings: 'Instellingen', knowledge: 'Kennis', usage: 'Gebruik', briefing: 'Samenvattingen', releases: 'Versie', excel: 'Excel', whatsapp: 'WhatsApp' };

let seq = 0;
export function newTabId(kind: TabKind): string {
  seq += 1;
  return `${kind}-${Date.now().toString(36)}-${seq}`;
}

export function initialTabs(): TabsState {
  const mail: Tab = { id: 'mail-1', kind: 'mail', title: 'Mail', state: {} };
  return { tabs: { [mail.id]: mail }, panes: { left: { id: 'left', tabIds: [mail.id], activeTabId: mail.id }, right: { id: 'right', tabIds: [], activeTabId: null } }, paneCount: 1, activePane: 'left' };
}

export const tabs = createStore<TabsState>(initialTabs());

/** De tabstaat zoals hij na herladen terugkomt uit localStorage: alleen wat klopt, anders de huidige stand (`cur`). De ongelezen
 * gesprekken (`unread`) komen mee, zodat een stip na herladen blijft tot het gesprek in beeld is. */
export function restoreTabs(saved: Partial<TabsState>, cur: TabsState): TabsState {
  const s = saved;
  if (!s.tabs || !s.panes || !s.panes.left || !s.panes.right) return cur;
  const valid = (ids: string[]) => ids.filter((id) => s.tabs![id]);
  const left = { ...s.panes.left, tabIds: valid(s.panes.left.tabIds ?? []) };
  const right = { ...s.panes.right, tabIds: valid(s.panes.right.tabIds ?? []) };
  const fix = (p: Pane): Pane => ({ ...p, activeTabId: p.activeTabId && p.tabIds.includes(p.activeTabId) ? p.activeTabId : (p.tabIds[0] ?? null) });
  const state: TabsState = { tabs: s.tabs, panes: { left: fix(left), right: fix(right) }, paneCount: s.paneCount === 2 ? 2 : 1, activePane: s.activePane === 'right' ? 'right' : 'left' };
  if (!state.panes.left.tabIds.length && !state.panes.right.tabIds.length) return cur;
  if (Array.isArray(s.unread)) state.unread = s.unread.filter((id): id is string => typeof id === 'string').slice(-UNREAD_KEPT);
  return state;
}

if (typeof localStorage !== 'undefined') persist(tabs, 'jerome2.tabs', (v) => v, restoreTabs);

function paneOfTab(state: TabsState, tabId: string): Pane | null {
  return (Object.values(state.panes) as Pane[]).find((p) => p.tabIds.includes(tabId)) ?? null;
}

/** Opent een tabblad van deze soort in het actieve paneel (op de telefoon: links); bestaat er al één (bij `single`), dan wordt dat actief. */
export function openTab(kind: TabKind, opts: { title?: string; state?: Record<string, unknown>; single?: boolean; pane?: PaneId; reuse?: boolean } = {}): string {
  const s = tabs.get();
  const paneId = opts.pane ?? (app.get().narrow ? 'left' : s.activePane);
  const existing = opts.single || opts.reuse ? (Object.values(s.tabs) as Tab[]).find((t) => t.kind === kind) : undefined;
  if (existing) {
    const p = paneOfTab(s, existing.id);
    tabs.set({
      ...s,
      tabs: opts.state ? { ...s.tabs, [existing.id]: { ...existing, state: { ...existing.state, ...opts.state } } } : s.tabs,
      panes: p ? { ...s.panes, [p.id]: { ...p, activeTabId: existing.id } } : s.panes,
      activePane: p?.id ?? s.activePane,
    });
    return existing.id;
  }
  const id = newTabId(kind);
  const tab: Tab = { id, kind, title: opts.title ?? TAB_TITLES[kind], state: opts.state ?? {} };
  const pane = s.panes[paneId];
  tabs.set({ ...s, tabs: { ...s.tabs, [id]: tab }, panes: { ...s.panes, [paneId]: { ...pane, tabIds: [...pane.tabIds, id], activeTabId: id } }, activePane: paneId });
  return id;
}

export function activateTab(tabId: string): void {
  const s = tabs.get();
  const p = paneOfTab(s, tabId);
  if (!p) return;
  tabs.set({ ...s, panes: { ...s.panes, [p.id]: { ...p, activeTabId: tabId } }, activePane: p.id });
}

export function closeTab(tabId: string): void {
  const s = tabs.get();
  const p = paneOfTab(s, tabId);
  if (!p) return;
  const tabIds = p.tabIds.filter((id) => id !== tabId);
  const idx = p.tabIds.indexOf(tabId);
  const activeTabId = p.activeTabId === tabId ? (tabIds[Math.min(idx, tabIds.length - 1)] ?? null) : p.activeTabId;
  const rest = { ...s.tabs };
  delete rest[tabId];
  tabs.set({ ...s, tabs: rest, panes: { ...s.panes, [p.id]: { ...p, tabIds, activeTabId } } });
}

export function renameTab(tabId: string, title: string): void {
  const s = tabs.get();
  const t = s.tabs[tabId];
  if (!t) return;
  tabs.set({ ...s, tabs: { ...s.tabs, [tabId]: { ...t, title: title.trim() || t.title } } });
}

/** De titel van het tabblad van een gesprek: "Chat: <titel>" bij een gewoon gesprek, anders de titel zelf. */
export function chatTabTitle(chat: Pick<Chat, 'kind' | 'title'>): string {
  return (chat.kind === 'chat' ? `Chat: ${chat.title}` : chat.title).trim();
}

/** Hoort dit chattabblad bij een mail (ticket 30961, `state.mail`, zie state/mailTabs.ts)? Dan houdt het het onderwerp als titel. */
export function isMailTab(tab: Pick<Tab, 'kind' | 'state'>): boolean {
  return tab.kind === 'chat' && Boolean(tab.state['mail']);
}

/** Een gesprek kreeg een nieuwe titel (de titel uit het model, `chat.updated`): elk chattabblad van dat gesprek krijgt hem, ook
 * een tabblad dat niet in beeld is; het chatscherm hernoemt alleen het tabblad dat het toont. Een tabblad van een mail (ticket
 * 30961) niet: dat heet naar het onderwerp van de mail, zodat de eigenaar het in de tabbalk terugvindt naast de kolom Gevlagd. */
export function retitleChatTabs(chat: Pick<Chat, 'id' | 'kind' | 'title'>): void {
  const title = chatTabTitle(chat);
  if (!title) return;
  const s = tabs.get();
  const changed = (Object.values(s.tabs) as Tab[]).filter((t) => t.kind === 'chat' && t.state['chatId'] === chat.id && t.title !== title && !isMailTab(t));
  if (!changed.length) return;
  const next = { ...s.tabs };
  for (const t of changed) next[t.id] = { ...t, title };
  tabs.set({ ...s, tabs: next });
}

export function updateTabState(tabId: string, patch: Record<string, unknown>): void {
  const s = tabs.get();
  const t = s.tabs[tabId];
  if (!t) return;
  let changed = false;
  for (const [k, v] of Object.entries(patch)) if (!Object.is(t.state[k], v)) changed = true;
  if (!changed) return;
  tabs.set({ ...s, tabs: { ...s.tabs, [tabId]: { ...t, state: { ...t.state, ...patch } } } });
}

export function moveTab(tabId: string, to: PaneId, index?: number): void {
  const s = tabs.get();
  const from = paneOfTab(s, tabId);
  if (!from) return;
  const fromIds = from.tabIds.filter((id) => id !== tabId);
  const target = from.id === to ? { ...from, tabIds: fromIds } : s.panes[to];
  const toIds = [...target.tabIds];
  toIds.splice(index ?? toIds.length, 0, tabId);
  const panes = { ...s.panes };
  panes[from.id] = { ...from, tabIds: fromIds, activeTabId: from.activeTabId === tabId && from.id !== to ? (fromIds[0] ?? null) : from.activeTabId };
  panes[to] = { ...target, tabIds: toIds, activeTabId: tabId };
  tabs.set({ ...s, panes, paneCount: to === 'right' ? 2 : s.paneCount, activePane: to });
}

export function setPaneCount(count: 1 | 2): void {
  const s = tabs.get();
  if (count === 1 && s.panes.right.tabIds.length) {
    // Tabbladen van rechts gaan naar links.
    const left = { ...s.panes.left, tabIds: [...s.panes.left.tabIds, ...s.panes.right.tabIds] };
    tabs.set({ ...s, paneCount: 1, activePane: 'left', panes: { left: { ...left, activeTabId: left.activeTabId ?? left.tabIds[0] ?? null }, right: { id: 'right', tabIds: [], activeTabId: null } } });
    return;
  }
  tabs.set({ ...s, paneCount: count });
}

export function setActivePane(id: PaneId): void {
  const s = tabs.get();
  if (s.activePane !== id) tabs.set({ ...s, activePane: id });
}
